import { useState, useMemo } from "react";
import {
  useDeleteCampaignsByCampaignBugsAndBugIdMediaMediaIdMutation,
  usePatchUsersMeCampaignsByCampaignIdBugsAndBugIdMutation,
  usePostCampaignsByCampaignBugsAndBugIdMediaMutation,
} from "../../../services/tryberApi";
import { BugEditValues } from "./types";

interface UseBugEditProps {
  campaignId: string;
  bugId?: number;
  currentMedia?: { id: number }[];
  minimumMedia: number;
  onSaved: () => Promise<unknown>;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function useBugEdit({
  campaignId,
  bugId,
  currentMedia = [],
  minimumMedia,
  onSaved,
  onSuccess,
  onError,
}: UseBugEditProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<BugEditValues>({});
  const [mediaToRemove, setMediaToRemove] = useState<number[]>([]);
  const [newMediaFiles, setNewMediaFiles] = useState<File[]>([]);

  const [patchBug, { isLoading: isSaving }] =
    usePatchUsersMeCampaignsByCampaignIdBugsAndBugIdMutation();
  const [uploadBugMedia, { isLoading: isUploading }] =
    usePostCampaignsByCampaignBugsAndBugIdMediaMutation();
  const [deleteBugMedia] =
    useDeleteCampaignsByCampaignBugsAndBugIdMediaMediaIdMutation();

  const totalMediaCount = useMemo(() => {
    const remainingMedia = currentMedia.filter(
      (m) => !mediaToRemove.includes(m.id),
    ).length;
    return remainingMedia + newMediaFiles.length;
  }, [currentMedia, mediaToRemove, newMediaFiles]);

  const isMediaValid = totalMediaCount >= minimumMedia;

  const patchField = <K extends keyof BugEditValues>(
    key: K,
    value: BugEditValues[K],
  ) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const startEditing = () => setIsEditing(true);

  const resetState = () => {
    setIsEditing(false);
    setEditedValues({});
    setMediaToRemove([]);
    setNewMediaFiles([]);
  };

  const handleCancel = () => resetState();

  const handleSave = async () => {
    if (!bugId) return;
    try {
      if (mediaToRemove.length > 0) {
        await Promise.all(
          mediaToRemove.map((mediaId) =>
            deleteBugMedia({
              campaign: campaignId,
              bugId: bugId.toString(),
              mediaId: mediaId.toString(),
            }).unwrap(),
          ),
        );
      }

      let newMedia: BugEditValues["media"] = [];
      if (newMediaFiles.length > 0) {
        const formData = new FormData();
        newMediaFiles.forEach((file) => {
          formData.append("media", file, file.name);
        });

        const uploadResult = await uploadBugMedia({
          campaign: campaignId,
          bugId: bugId.toString(),
          body: formData as unknown as { media: Blob[] },
        }).unwrap();

        const uploadedAt = new Date().toISOString();
        newMedia = uploadResult.files.map((f) => ({
          location: f.path,
          type: f.mimetype,
          uploaded_date: uploadedAt,
        }));
      }

      const patchBody: BugEditValues = { ...editedValues };
      if (newMedia && newMedia.length > 0) {
        patchBody.media = newMedia;
      }

      await patchBug({
        campaignId,
        bugId: bugId.toString(),
        body: patchBody,
      }).unwrap();

      await onSaved();
      resetState();
      onSuccess?.();
    } catch (err) {
      console.error("Failed to update bug:", err);
      onError?.(err);
    }
  };

  return {
    isEditing,
    isSaving,
    isUploading,
    editedValues,
    mediaToRemove,
    newMediaFiles,
    totalMediaCount,
    isMediaValid,
    startEditing,
    handleCancel,
    handleSave,
    patchField,
    setEditedValues,
    setMediaToRemove,
    setNewMediaFiles,
  };
}
